import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from './ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import '../../style/scss/contact-merge.scss';

// One group of possible duplicate contacts
export interface ContactDuplicateGroup {
    ids: number[];
    names: string[];
    reasons: string[];
}

interface ContactDuplicatesModalProps {
    groups: ContactDuplicateGroup[];
    truncated?: boolean;
    onMerge: (ids: number[]) => void;
}

/*
 * ContactDuplicatesModal.tsx
 * Lists the groups of possible duplicate contacts found for a contact
 * list, with the reason they match and a merge button per group which
 * opens the merge preview page.
 */
const ContactDuplicatesModal: React.FC<ContactDuplicatesModalProps> = ({ groups, truncated, onMerge }) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();

    // Reveal the modal on first render
    useEffect(() => { revealModal() }, []);

    // Close the modal and open the merge page of a group
    const handleMerge = (ids: number[]) => {
        closeModal();
        onMerge(ids);
    };

    // Get the translated reasons of a group
    const getReasonText = (reasons: string[]) => reasons.map(reason => t(`contact.merge.duplicate_reason_${reason}`)).join(', ');

    return (
        <div className='contact-duplicates-modal'>
            <p className='contact-duplicates-explanation'>{t('contact.merge.duplicates_modal_explanation')}</p>
            {groups.map(group => (
                <div key={group.ids.join('-')} className='contact-duplicates-group'>
                    <div>
                        <div className='contact-duplicates-names'>{group.names.join(' · ')}</div>
                        <div className='contact-duplicates-reason'>{getReasonText(group.reasons)}</div>
                    </div>
                    <SecondaryButton
                        onClick={() => handleMerge(group.ids)}
                        label='button.merge_button_label'
                        size='small' />
                </div>
            ))}
            {truncated && (
                <p className='contact-duplicates-truncated'>{t('contact.merge.duplicates_truncated')}</p>
            )}
        </div>
    );
};

export default ContactDuplicatesModal;
